import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Box, CircularProgress, IconButton, Menu, MenuItem, Tooltip, Typography, useMediaQuery } from "@mui/material"
import { setActiveAnalysisPanelTab } from "@/slices/chatSlice"
import { useDispatch, useSelector } from "react-redux"
import { AnalysisPanelTabs } from "@/components/AnalysisPanel"
import { DocumentLoadEvent, PageChangeEvent, SpecialZoomLevel, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"
import { defaultLayoutPlugin, ToolbarProps, ToolbarSlot } from "@react-pdf-viewer/default-layout"
import { RenderSearchProps, searchPlugin, SearchProps } from "@react-pdf-viewer/search"
import { RenderZoomInProps, RenderZoomOutProps, zoomPlugin } from "@react-pdf-viewer/zoom"
import { CustomMoreActionsPopover } from "@/components/CitationPDFViewer/MoreActionsPopover"
import Fuse from "fuse.js"
import { processFileName } from "@/pages/documents/Documents"
import { getFilePlugin, RenderDownloadProps } from "@react-pdf-viewer/get-file"
import theme from "@/theme"
import { t } from "i18next"
import { handleTranslatePDF } from "./PDFTranslation"
import { getSavedLanguage } from "@/i18n"
import {
    ChevronDown,
    ChevronUp,
    Download as DownloadIcon,
    Languages,
    Maximize2,
    Search as SearchIcon,
    X,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
} from "lucide-react"
import { generatePath } from "react-router-dom"
import { RoutesPath } from "@/router/config"
import { CustomLink } from "@/components/CustomLink/CustomLink"
import { onDownload } from "@/utils/download"
import { TranslateDownloadModal } from "@/components/CitationPDFViewer/FileTranslate"
import { useModal } from "@/hooks/useModal"
import * as s from "./styles"

export interface IHighlightArray {
    simple: string[]
    semantic: string[]
    answer: string[]
}

interface ICitationPDFViewerProps {
    pdfBlob: Blob
    fileName: string
    id?: string
    large?: boolean
    initialPage: number
    highlightArray?: IHighlightArray
    handleClose?: () => void | undefined
    autoHeight?: boolean | undefined
    language?: string
}

interface CustomSearchProps {
    Search: React.ComponentType<SearchProps>
    setTargetPages: (targetPage: (page: any) => boolean) => void
}

export const enum CitationsHighlightMode {
    ANSWER_PRIORITY = "answer_priority",
    SEMANTIC_PRIORITY = "semantic_priority",
    SIMPLE_PRIORITY = "simple_priority",
    ONLY_ANSWER = "only_answer",
    ONLY_SEMANTIC = "only_semantic",
    ONLY_SIMPLE = "only_simple",
}

function splitTextIntoChunks(text: string, minChunkSize = 5) {
    const chunks = []
    let currentChunk = ""

    // Split while preserving numbers with dots/commas
    // This regex splits on whitespace and punctuation, but NOT when dots/commas are between digits
    const segments = text.split(/(\s+|(?<!\d)[,;.!?—-]+(?!\d))/)

    for (const segment of segments) {
        // Add segment to current chunk
        currentChunk += segment

        // Check if we should finalize this chunk
        const endsWithPunctuation = /(?<!\d)[.,!?](?!\d)$/.test(currentChunk.trim())
        const isLargeEnough = currentChunk.length >= minChunkSize

        if (endsWithPunctuation && isLargeEnough) {
            chunks.push(currentChunk.trim())
            currentChunk = ""
        } else if (currentChunk.length > 100) {
            // Force split on whitespace if chunk gets too large
            const lastSpaceIndex = currentChunk.lastIndexOf(" ")
            if (lastSpaceIndex > minChunkSize) {
                chunks.push(currentChunk.substring(0, lastSpaceIndex).trim())
                currentChunk = currentChunk.substring(lastSpaceIndex + 1)
            }
        }
    }

    // Add remaining text
    if (currentChunk.trim().length >= minChunkSize) {
        chunks.push(currentChunk.trim())
    } else if (chunks.length > 0 && currentChunk.trim().length > 0) {
        // Merge small remainder with last chunk
        chunks[chunks.length - 1] += " " + currentChunk.trim()
    } else if (currentChunk.trim().length > 0) {
        // Edge case: only one very small chunk
        chunks.push(currentChunk.trim())
    }

    return chunks
}

function findFuzzyMatchesInPdfContent(highlights: any[], content: string, threshold = 0.4) {
    // Extract <em> content and strip all HTML tags
    const highlightsToSearch = [...new Set(extractText(highlights))]

    if (highlightsToSearch.length === 0) {
        return []
    }

    const fuseOptions = {
        includeScore: true,
        findAllMatches: true,
        threshold: threshold, // Lower means more strict matching
    }

    const contentChunksWithNumbers = splitTextIntoChunks(content)
    const fuse = new Fuse(contentChunksWithNumbers, fuseOptions)

    let matches: string[] = []

    highlightsToSearch.forEach(highlight => {
        const searchResults = fuse.search(highlight)
        searchResults.forEach(({ item }) => {
            matches.push(item)
        })
    })

    // Remove duplicates
    matches = [...new Set(matches)]

    return matches
}

function stripAllHtml(text: string): string {
    return text
        .replace(/<[^>]+>/g, " ") // strip every tag
        .replace(/\s+/g, " ") // normalise whitespace
        .trim() // remove surrounding spaces
}

function extractText(highlights: string[]): string[] {
    const extracted: string[] = []

    highlights.forEach(highlight => {
        const clean = stripAllHtml(highlight)
        if (clean) {
            extracted.push(clean)
        }
    })

    return extracted
}

function processHighlightsBasedOnPriority(
    highlightPriorities: string[][],
    CurrentPageTextString: string,
    thresholds: number[]
): string[] {
    let result: string[] = []
    for (let i = 0; i < highlightPriorities.length; i++) {
        result = findFuzzyMatchesInPdfContent(highlightPriorities[i], CurrentPageTextString, thresholds[i])
        if (result.length > 0) break
    }
    return result
}

function getHighlights(
    highlightArray: {
        simple: string[]
        semantic: string[]
        answer: string[]
    },
    CurrentPageTextString: string,
    citationsHighlightMode: CitationsHighlightMode
): string[] {
    let toHighlight: string[] = []

    const allArrays = [...highlightArray.simple, ...highlightArray.semantic, ...highlightArray.answer]

    toHighlight = processHighlightsBasedOnPriority(
        [
            highlightArray.semantic,
            highlightArray.semantic,
            highlightArray.simple,
            highlightArray.answer,
            allArrays,
            allArrays,
            allArrays,
            allArrays,
        ],
        CurrentPageTextString,
        [0.1, 0.2, 0.2, 0.2, 0.3, 0.4, 0.5, 0.6]
    )

    return toHighlight
}

const SEARCH_DEBOUNCE_MS = 250
const SEARCH_MIN_LENGTH = 2
const SEARCH_MAX_MATCHES = 500

// Bridges render-prop numberOfMatches into a hook-friendly useEffect.
// Skips the initial mount call so it only fires when the count actually changes.
function MatchCountSync({ count, onSettled }: { count: number; onSettled: () => void }) {
    const onSettledRef = useRef(onSettled)
    onSettledRef.current = onSettled
    const mountedRef = useRef(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!mountedRef.current) { mountedRef.current = true; return }
        onSettledRef.current()
    }, [count])
    return null
}

function CustomSearch({ Search, setTargetPages }: Readonly<CustomSearchProps>) {
    const [searchTerm, setSearchTerm] = useState("")
    const [debouncedTerm, setDebouncedTerm] = useState("")
    const [isSearching, setIsSearching] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [isPrevHovered, setIsPrevHovered] = useState(false)
    const [isNextHovered, setIsNextHovered] = useState(false)
    const searchPropsRef = useRef<RenderSearchProps | null>(null)
    const setTargetPagesRef = useRef(setTargetPages)
    setTargetPagesRef.current = setTargetPages

    useEffect(() => {
        const id = setTimeout(() => setDebouncedTerm(searchTerm), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(id)
    }, [searchTerm])

    useEffect(() => {
        const props = searchPropsRef.current
        if (!props) return
        if (debouncedTerm.length < SEARCH_MIN_LENGTH) {
            props.clearKeyword()
            setIsSearching(false)
            return
        }
        setTargetPagesRef.current(() => true)
        props.setKeyword(debouncedTerm)
        setIsSearching(true)
        // setKeyword queues a React state update inside the plugin; search() must
        // run after that update is applied, otherwise it reads the previous keyword.
        const id = setTimeout(() => searchPropsRef.current?.search(), 0)
        return () => clearTimeout(id)
    }, [debouncedTerm])

    // Fallback: clear spinner if numberOfMatches didn't change between searches
    // (edge case where two different terms return the same count).
    useEffect(() => {
        if (!isSearching) return
        const id = setTimeout(() => setIsSearching(false), 1500)
        return () => clearTimeout(id)
    }, [isSearching])

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    }, [])

    return (
        <Search>
            {(renderSearchProps: RenderSearchProps) => {
                searchPropsRef.current = renderSearchProps

                const showResults = debouncedTerm.length >= SEARCH_MIN_LENGTH
                const tooMany = renderSearchProps.numberOfMatches > SEARCH_MAX_MATCHES
                const navDisabled = isSearching || renderSearchProps.numberOfMatches === 0 || tooMany

                return (
                    <div style={s.getSearchWrapper(isFocused)}>
                        <SearchIcon size={14} style={s.searchIcon} />
                        <input
                            placeholder={t("Search") || "Search"}
                            type="text"
                            value={searchTerm}
                            onChange={handleInputChange}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            style={s.searchInput}
                        />
                        {showResults && (
                            <>
                                <MatchCountSync
                                    count={renderSearchProps.numberOfMatches}
                                    onSettled={() => setIsSearching(false)}
                                />
                                <span
                                    className="notranslate"
                                    style={isSearching || renderSearchProps.numberOfMatches > 0 ? s.matchCount : s.notFoundText}
                                >
                                    {isSearching
                                        ? <CircularProgress size={10} sx={{ color: "text.secondary" }} />
                                        : tooMany
                                        ? t("Too many matches")
                                        : renderSearchProps.numberOfMatches > 0
                                        ? `${renderSearchProps.currentMatch}/${renderSearchProps.numberOfMatches}`
                                        : t("Not found")}
                                </span>
                                <button
                                    style={{
                                        ...s.navButton,
                                        ...(isPrevHovered ? s.navButtonHovered : {}),
                                        ...(navDisabled ? { opacity: 0.3, pointerEvents: "none" as const } : {}),
                                    }}
                                    onClick={renderSearchProps.jumpToPreviousMatch}
                                    onMouseEnter={() => setIsPrevHovered(true)}
                                    onMouseLeave={() => setIsPrevHovered(false)}
                                >
                                    <Tooltip title={t("Previous match")}>
                                        <span style={{ display: "flex" }}>
                                            <ChevronUp size={14} />
                                        </span>
                                    </Tooltip>
                                </button>
                                <button
                                    style={{
                                        ...s.navButton,
                                        ...(isNextHovered ? s.navButtonHovered : {}),
                                        ...(navDisabled ? { opacity: 0.3, pointerEvents: "none" as const } : {}),
                                    }}
                                    onClick={renderSearchProps.jumpToNextMatch}
                                    onMouseEnter={() => setIsNextHovered(true)}
                                    onMouseLeave={() => setIsNextHovered(false)}
                                >
                                    <Tooltip title={t("Next match")}>
                                        <span style={{ display: "flex" }}>
                                            <ChevronDown size={14} />
                                        </span>
                                    </Tooltip>
                                </button>
                            </>
                        )}
                    </div>
                )
            }}
        </Search>
    )
}

export const CitationPDFViewer: React.FC<ICitationPDFViewerProps> = ({
    highlightArray,
    initialPage,
    pdfBlob,
    fileName,
    id,
    large,
    handleClose,
    autoHeight,
}) => {
    const [page, setPage] = useState(initialPage - 1)
    const dispatch = useDispatch()
    const [fileUrl, setFileUrl] = useState<string>(URL.createObjectURL(pdfBlob))
    const originalFileUrl = URL.createObjectURL(pdfBlob)
    const citationsHighlightMode = useSelector((state: any) => state.chat.citationsHighlightMode)

    const [isTranslating, setIsTranslating] = useState(false)
    const [currentLanguage, setCurrentLanguage] = useState("original")

    const currentPage = useRef(0)
    const originalPage = useRef(0)
    const viewerContainerRef = useRef<HTMLDivElement>(null)

    const [totalPages, setTotalPages] = useState(0)

    const [pdfDocument, setPdfDocument] = useState(null)
    const [targetLanguageCode, setTargetLanguageCode] = useState(getSavedLanguage())

    const [fileNameCurrent, setFileNameCurrent] = useState<string>(fileName)
    const [isFullFileTranslated, setIsFullFileTranslated] = useState(false)
    const [downloadMenuPosition, setDownloadMenuPosition] = useState<{ top: number; left: number } | null>(null)
    const [translateModalOpen, setTranslateModalOpen] = useState(false)
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(large ? 600 : 1200))
    const { confirm } = useModal()
    const isConfirmingFullTranslation = useRef(false)
    const forceLanguageTranslation = useRef(false)

    // Plugin factories must be called unconditionally — they use React hooks (useMemo) internally
    const renderToolbarRef = useRef<(Toolbar: (props: ToolbarProps) => ReactElement) => ReactElement>()

    const getFilePluginInstance = getFilePlugin({ fileNameGenerator: () => fileNameCurrent })
    const { Download } = getFilePluginInstance
    const searchPluginInstance = searchPlugin({ keyword: [] })
    const { highlight, setTargetPages } = searchPluginInstance

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: () => [],
        renderToolbar: (Toolbar) => renderToolbarRef.current!(Toolbar),
    })
    const zoomPluginInstance = zoomPlugin()
    const { zoomTo } = zoomPluginInstance

    // Plugin factories return new wrapper objects/functions each render, but their
    // internal stores use useMemo(fn,[]) and are stable. Capture first-render references
    // so the Viewer and toolbar receive stable identities across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const plugins = useMemo(() => [defaultLayoutPluginInstance, searchPluginInstance, zoomPluginInstance, getFilePluginInstance], [])
    // Search component must keep stable identity — otherwise React unmounts/remounts
    // it on every parent re-render, wiping the keyword and match state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const StableSearchComponent = useMemo(() => searchPluginInstance.Search, [])
    // Bound to our zoomPluginInstance store so its display stays in sync with
    // both user-driven zoom changes and imperative zoomTo() calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const StableZoomComponent = useMemo(() => zoomPluginInstance.Zoom, [])

    const getTotalPages = async () => {
        if (pdfDocument) {
            // @ts-ignore
            return pdfDocument.numPages
        }
        return 0
    }
    const handleTranslate = handleTranslatePDF({
        id,
        currentPage,
        setIsTranslating,
        currentLanguage,
        setFileUrl,
        originalFileUrl,
        setFileNameCurrent,
        fileName,
        setCurrentLanguage,
        setPage,
        originalPage,
        setIsFullFileTranslated,
        totalPages,
        targetLanguageCode,
        confirm,
        isConfirmingFullTranslation,
        forceLanguageTranslation,
    })

    useEffect(() => {
        if (pdfDocument) {
            getTotalPages().then(pages => setTotalPages(pages))
        }
    }, [pdfDocument])

    if (!fileUrl) {
        dispatch(setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab))

        return null
    }

    const processHighlights = (highlights: string[]): string[] => {
        if (!highlights) {
            return []
        }
        const processedHighlights: string[] = []
        for (const item of highlights) {
            // Remove HTML tags EXCEPT <em> tags, replace with space
            const sentence = item
                .replace(/<(?!\/?em\b)[^>]*>/g, " ")
                // Normalize multiple spaces to single space
                .replace(/\s+/g, " ")
                .trim()

            // Skip too short
            if (sentence.length > 4) {
                processedHighlights.push(sentence)
            }
        }
        return processedHighlights
    }

    const processAllHighlightTypes = (highlightArray: any) => {
        return {
            simple: processHighlights(highlightArray.simple),
            semantic: processHighlights(highlightArray.semantic),
            answer: processHighlights(highlightArray.answer),
        }
    }

    const highlightArrayRes = processAllHighlightTypes(highlightArray)

    const handleDocumentLoad = async (e: DocumentLoadEvent) => {
        // @ts-ignore
        setPdfDocument(e.doc)
        if (highlightArrayRes.simple.length + highlightArrayRes.semantic.length + highlightArrayRes.answer.length > 0) {
            const numberOfPages = e.doc.numPages
            const currentPageNumber = Math.min(page + 1, numberOfPages)
            const CurrentPage = await e.doc.getPage(currentPageNumber)
            const CurrentPageText = await CurrentPage.getTextContent()

            // console.log("CurrentPageText", CurrentPageText)
            const CurrentPageTextString = CurrentPageText.items.map(item => item.str).join("")
            // console.log("CurrentPageTextString", CurrentPageTextString)
            const toHighlight = getHighlights(highlightArrayRes, CurrentPageTextString, citationsHighlightMode)

            // console.log("toHighlight", toHighlight)
            if (toHighlight.length > 0) {
                setTargetPages(targetPage => targetPage.pageIndex === currentPageNumber - 1)
            }
            if (toHighlight.length > 0) {
                highlight([...toHighlight])
            }
        }

        setTimeout(
            () => {
                zoomTo(SpecialZoomLevel.PageWidth)
            },
            Math.min(1.5 * initialPage + 500, 1000)
        )
    }

    const handlePageChange = useCallback((e: PageChangeEvent) => {
        currentPage.current = e.currentPage
    }, [])

    function translateComponent() {
        const handleTranslateMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setTranslateModalOpen(true)
        }

        const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setDownloadMenuPosition({ top: rect.bottom, left: rect.left })
        }

        const handleDownloadMenuClose = () => {
            setDownloadMenuPosition(null)
        }

        const handleTranslateDownload = ({
            translateTo,
            translateImages,
            currentPageOnly,
            translateSourceFile,
        }: {
            translateTo: string
            translateImages: boolean
            currentPageOnly: boolean
            translateSourceFile: boolean
        }) => {
            if (id !== undefined) {
                onDownload({
                    path: fileName || "download",
                    forceFilename: !translateSourceFile,
                    fileId: id,
                    downloadTranslater: true,
                    translateTo: translateTo,
                    currentPageOnly: currentPageOnly,
                    page: currentPageOnly ? currentPage.current : undefined,
                    // totalPages: totalPages,
                    downloadSource: translateSourceFile,
                    confirm: confirm,
                })
            }
        }
        return (
            <>
                {currentLanguage === "original" ? (
                    <Tooltip title={isTranslating ? `${t("Translating")}...` : t("Translate")}>
                        <span>
                            <IconButton
                                className="notranslate"
                                onClick={handleTranslateMenuOpen}
                                disabled={isTranslating}
                                sx={s.toolbarIconBtn}
                            >
                                {isTranslating ? <CircularProgress size={14} /> : <Languages size={16} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip title={t("Original")}>
                        <span>
                            <IconButton
                                className="notranslate"
                                onClick={() => handleTranslate("page", "en", false)}
                                sx={s.toolbarIconBtn}
                            >
                                <Languages size={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <TranslateDownloadModal
                    open={translateModalOpen}
                    onClose={() => setTranslateModalOpen(false)}
                    onConfirm={handleTranslateDownload}
                    onTranslateAndDisplay={({ translateTo, currentPageOnly, translateSourceFile }) => {
                        const action = currentPageOnly ? "page" : "full"
                        handleTranslate(action, translateTo, translateSourceFile)
                    }}
                    fileName={fileName}
                    currentPage={currentPage}
                    totalPages={totalPages}
                />

                <Download>
                    {(props: RenderDownloadProps) => (
                        <>
                            <Tooltip title={t("Download")}>
                                <span>
                                    <IconButton
                                        className="notranslate"
                                        onClick={handleDownloadMenuOpen}
                                        sx={s.toolbarIconBtn}
                                    >
                                        <DownloadIcon size={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Menu
                                anchorReference="anchorPosition"
                                anchorPosition={downloadMenuPosition ?? undefined}
                                open={Boolean(downloadMenuPosition)}
                                onClose={handleDownloadMenuClose}
                                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleDownloadMenuClose()
                                        props.onClick()
                                    }}
                                >
                                    {isFullFileTranslated
                                        ? t("Download translated file")
                                        : currentLanguage === "translated"
                                          ? t("Download translated page")
                                          : t("Download pdf File")}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleDownloadMenuClose()
                                        if (id !== undefined) {
                                            onDownload({
                                                path: fileName || "download",
                                                fileId: id,
                                                downloadSource: true,
                                                confirm: confirm,
                                            })
                                        }
                                    }}
                                >
                                    {t("Download original file")}
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Download>
            </>
        )
    }

    const handleFullScreen = async () => {
        try {
            await viewerContainerRef.current?.requestFullscreen()
        } catch (err) {
            console.error("Fullscreen request failed:", err)
        }
    }

    const isCitationMode =
        highlightArray &&
        highlightArray.simple.length + highlightArray.semantic.length + highlightArray.answer.length > 0

    const renderToolbar = (Toolbar: (props: ToolbarProps) => ReactElement): ReactElement => (
        <Toolbar>
            {(slots: ToolbarSlot): ReactElement => {
                const { ZoomIn, ZoomOut, CurrentPageLabel } = slots
                return (
                    <div style={s.toolbarContainer}>
                        {/* LEFT: Filename + Page pill */}
                        <div style={s.leftGroup}>
                            <CustomLink
                                underline="none"
                                color="inherit"
                                disabled={!isCitationMode}
                                to={generatePath(RoutesPath.DocumentDetails, {
                                    id: id ?? null,
                                    name: fileName,
                                })}
                                style={s.getCustomLinkStyle(isSmallScreen)}
                            >
                                <Tooltip className="notranslate" title={fileName} placement="top">
                                    <Typography
                                        variant="inherit"
                                        className="notranslate"
                                        sx={s.fileNameSx}
                                    >
                                        {processFileName(fileNameCurrent.replace(".pdf", ""))}
                                    </Typography>
                                </Tooltip>
                            </CustomLink>
                            {totalPages > 1 && (
                                <span className="notranslate" style={s.pagePill}>
                                    <CurrentPageLabel>
                                        {({ currentPage, numberOfPages }) => (
                                            <>{currentPage + 1}/{numberOfPages}</>
                                        )}
                                    </CurrentPageLabel>
                                </span>
                            )}
                        </div>

                        {!isSmallScreen && <div style={s.toolbarDivider} />}

                        {/* CENTER: Search */}
                        <div style={s.centerGroup}>
                            <CustomSearch Search={StableSearchComponent} setTargetPages={setTargetPages} />
                        </div>

                        {!isSmallScreen && <div style={s.toolbarDivider} />}

                        {/* RIGHT: Zoom + Actions + Close */}
                        <div style={s.rightGroup}>
                            <div style={s.zoomGroup}>
                                <ZoomOut>
                                    {(props: RenderZoomOutProps) => (
                                        <Tooltip title={t("Zoom out")}>
                                            <button style={s.zoomButton} onClick={props.onClick}>
                                                <ZoomOutIcon size={15} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </ZoomOut>
                                <div className="notranslate" style={s.zoomMiddle}>
                                    <StableZoomComponent />
                                </div>
                                <ZoomIn>
                                    {(props: RenderZoomInProps) => (
                                        <Tooltip title={t("Zoom in")}>
                                            <button style={s.zoomButton} onClick={props.onClick}>
                                                <ZoomInIcon size={15} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </ZoomIn>
                            </div>
                            {id !== undefined && translateComponent()}
                            <Tooltip title={t("Full screen")}>
                                <IconButton onClick={handleFullScreen} sx={s.toolbarIconBtn}>
                                    <Maximize2 size={16} />
                                </IconButton>
                            </Tooltip>
                            <CustomMoreActionsPopover toolbarSlot={slots} fileName={fileName} id={id} />
                            {(!large || handleClose) && (
                                <IconButton
                                    onClick={() => {
                                        if (!large) {
                                            dispatch(setActiveAnalysisPanelTab(""))
                                        } else if (handleClose) {
                                            handleClose()
                                        }
                                    }}
                                    sx={s.toolbarIconBtnClose}
                                    aria-label={t("Close")}
                                >
                                    <X size={16} />
                                </IconButton>
                            )}
                        </div>
                    </div>
                )
            }}
        </Toolbar>
    )

    // Update the ref so the stable plugin always calls the latest renderToolbar
    renderToolbarRef.current = renderToolbar

    let height: string
    const heightCalc = large ? "calc(100vh - 150px)" : "calc(100vh - 150px)"

    if (autoHeight) {
        height = "auto"
    } else {
        height = heightCalc
    }

    return (
        <Box ref={viewerContainerRef} sx={s.getViewerContainerSx(height)}>
            <Box sx={s.getViewerInnerSx(height)}>
                <Viewer
                    fileUrl={fileUrl}
                    plugins={plugins}
                    onDocumentLoad={handleDocumentLoad}
                    onPageChange={handlePageChange}
                    {...(page !== 0 ? { initialPage: page } : {})}
                />
            </Box>
        </Box>
    )
}
